import React, { useContext, useRef } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { REMOVE_NEW_TAG_FROM_EVENT_MUTATION } from 'Queries/Mutations';
import { GET_EVENTS_OVERVIEW_DATA_QUERY, USER_STATUS_QUERY } from 'Queries/Queries';
import classnames from 'classnames';
import { ParticipantsListContext } from 'context/ParticipantsList';
import { QrScannerContext } from 'context/QrScanner';
import { withRouter } from 'react-router';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { EventCardMetaData } from 'components/Event/EventCardMetaData/EventCardMetaData';
import { isNewEntry } from 'components/OverviewLists/utils/isNewEntry';
import { Tags } from 'components/Tags/Tags';

import './EventCard.scss';

export const EventCard = withRouter(({ data: event, history, isPastEvent }) => {
  const {
    data: userStatusData,
    loading: userStatusLoading,
    error: userStatusError,
  } = useQuery(USER_STATUS_QUERY);

  const userStatus = userStatusData && userStatusData.userStatus;

  const { id: eventId, title, tags } = event;
  const [removeNewTag] = useMutation(REMOVE_NEW_TAG_FROM_EVENT_MUTATION, {
    refetchQueries: () => [{ query: GET_EVENTS_OVERVIEW_DATA_QUERY }],
    awaitRefetchQueries: true,
    variables: {
      eventId,
    },
  });
  const { setList } = useContext(ParticipantsListContext);
  const { setQrScannerOpen } = useContext(QrScannerContext);

  const cardRef = useRef(null);

  const isNew = isNewEntry(event.tags);
  const hasManyTags = (tags?.length ?? 0) > 3;

  const handleRegistrationClick = () => {
    history.push({
      pathname: event.path,
    });
    setList(false);
    setQrScannerOpen(false);
  };

  const handleEntryList = () => {
    history.push({
      pathname: event.path,
    });
    setList(true);
    setQrScannerOpen(false);
  };

  const handleQrScan = () => {
    history.push({
      pathname: event.path,
    });
    setQrScannerOpen(true);
    setList(false);
  };

  const handleClick = () => {
    handleRegistrationClick();
    removeNewTag();
  };

  const isPrivilegedUser = userStatus?.role === 'POWER_USER' || userStatus?.role === 'EVENT_USER';

  const eventCardClasses = classnames('event-card', {
    'event-card--new': isNew,
    'event-card--with-actions-row': isPrivilegedUser,
  });

  if (userStatusLoading) {
    return null;
  }
  if (userStatusError) {
    console.error(userStatusError);
    return null;
  }

  const actionButtons = (
    <div
      className={classnames('event-card__cta-container', {
        'event-card__cta-container--split': isPrivilegedUser,
      })}>
      {isPrivilegedUser && (
        <div className="event-card__power-user-buttons">
          {userStatus?.role === 'POWER_USER' && (
            <div className="event-card__participants-list-button-wrapper">
              <ActionButton
                title={event.meetingLink ? 'Teilnehmerliste und QR-Code' : 'Teilnehmerliste'}
                onClick={handleEntryList}
                inverted
              />
            </div>
          )}
          {(userStatus?.role === 'EVENT_USER' || userStatus?.role === 'POWER_USER') &&
            !event.meetingLink && (
              <div className="event-card__participants-list-button-wrapper">
                <ActionButton title="QR Code scannen" onClick={handleQrScan} inverted />
              </div>
            )}
        </div>
      )}
      {!isPastEvent && <ActionButton title="Anzeigen" onClick={handleClick} inverted />}
    </div>
  );

  return (
    <div ref={cardRef} className={eventCardClasses}>
      <div className="event-card__header-row">
        <div className="event-card__title">{title}</div>
        <div className="event-card__header-meta">
          {!hasManyTags && (
            <div className="event-card__tags">
              <Tags tags={tags} inverted={isNew} />
            </div>
          )}
        </div>
      </div>
      {hasManyTags && (
        <div className="event-card__meta-row">
          <div className="event-card__tags">
            <Tags tags={tags} inverted={isNew} />
          </div>
        </div>
      )}
      <div className="event-card__footer-row">
        <EventCardMetaData event={event} />
        {!isPrivilegedUser && actionButtons}
      </div>
      {isPrivilegedUser && <div className="event-card__actions-row">{actionButtons}</div>}
    </div>
  );
});
